// src/components/Dashboard/PlayerDashboard/MyAssignmentsWidget.jsx
// Shows pending assignments from coaches

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTasks, FaClock, FaArrowRight, FaExclamationCircle } from 'react-icons/fa';
import styles from './MyAssignmentsWidget.module.css';

const MyAssignmentsWidget = ({ assignments, selectedProgram, refreshTrigger }) => {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);

  // Sort by due date (soonest first)
  const sortedAssignments = [...(assignments || [])].sort((a, b) => {
    const dateA = new Date(a.dueDate || a.createdAt);
    const dateB = new Date(b.dueDate || b.createdAt);
    return dateA - dateB;
  });

  const displayedAssignments = showAll ? sortedAssignments : sortedAssignments.slice(0, 3);

  const formatDueDate = (dateStr) => {
    if (!dateStr) return 'No due date';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = date - now;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

    if (days < 0) return 'Overdue';
    if (days === 0) return 'Due today';
    if (days === 1) return 'Due tomorrow';
    if (days <= 7) return `Due in ${days} days`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getDueStatus = (dateStr) => {
    if (!dateStr) return 'normal';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = date - now;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

    if (days < 0) return 'overdue';
    if (days <= 2) return 'urgent';
    return 'normal';
  };

  const handleAssignmentClick = (assignment) => {
    // Navigate to TeamItemsPage with itemId to open quick view
    if (assignment.programId && assignment._id) {
      navigate(`/program/${assignment.programId}/team-items?itemId=${assignment._id}`);
    } else if (assignment.programId) {
      navigate(`/program/${assignment.programId}/team-items`);
    }
  };

  const handleViewAll = () => {
    if (selectedProgram) {
      navigate(`/program/${selectedProgram._id}/team-items`);
    } else {
      setShowAll(!showAll);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <FaTasks className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>My Assignments</h3>
        </div>
        {sortedAssignments.length > 0 && (
          <span className={styles.badge}>{sortedAssignments.length}</span>
        )}
      </div>

      <div className={styles.content}>
        {sortedAssignments.length === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>✅</span>
            <p>All caught up!</p>
            <span className={styles.emptyHint}>No pending assignments</span>
          </div>
        ) : (
          <>
            <div className={styles.assignmentsList}>
              {displayedAssignments.map((assignment, index) => {
                const status = getDueStatus(assignment.dueDate);
                return (
                  <div 
                    key={assignment._id || index}
                    className={`${styles.assignmentCard} ${styles[status]}`}
                    onClick={() => handleAssignmentClick(assignment)}
                  >
                    <div className={styles.assignmentIcon}>
                      {status === 'overdue' ? <FaExclamationCircle /> : '📋'}
                    </div>
                    <div className={styles.assignmentInfo}>
                      <span className={styles.assignmentTitle}>
                        {assignment.title || assignment.name || 'Assignment'}
                      </span>
                      <div className={styles.assignmentMeta}>
                        <span className={`${styles.dueDate} ${styles[status]}`}>
                          <FaClock /> {formatDueDate(assignment.dueDate)}
                        </span>
                        {assignment.programName && (
                          <span className={styles.programName}>
                            {assignment.programName}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {sortedAssignments.length > 3 && (
              <button className={styles.viewAllBtn} onClick={handleViewAll}>
                {showAll ? 'Show less' : `See all ${sortedAssignments.length} assignments`} 
                <FaArrowRight />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyAssignmentsWidget;

